import React, { useEffect, useRef, useState } from "react";
import { Form, Select, Button, Row, Col, Card } from "antd";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "store/hook";
import { useDeviceStatus } from "Components/Dashboard/queries";
import { useFilter } from "Utility/hooks/useFilter";
import { TableData } from "Utility/Types/status_identity_get_set";
import { initialState, setFilter } from "Components/Topology/Device/deviceTableSlice";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { useMDCollapse } from "Utility/hooks/mdCollapse";
import { columnSetting } from "./DeviceTableC";
import { isEqual } from "cypress/types/lodash";
interface filterProps<T> {
  setDeviceTableData: (arg: T[]) => void;
  columnSetting: columnSetting;
}

const { Option } = Select;

type options<T> = {
  value: T;
  label: string;
};

//up:Good avoid user confusing
export const healthoptions: options<"up" | "critical" | "warning" | "offline">[] = [
  { value: "up", label: "Good" },
  { value: "critical", label: "Critical" },
  { value: "warning", label: "Warning" },
  { value: "offline", label: "Offline" },
];

export const strengthoptions: options<"excellent" | "good" | "fair" | "poor" | "na">[] = [
  { value: "excellent", label: "Excellent" },
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" },
  { value: "poor", label: "Poor" },
  { value: "na", label: "NA" },
];

const Filter = React.memo(({ setDeviceTableData, columnSetting }: filterProps<Record<keyof TableData, any>>) => {
  const disp = useAppDispatch();
  const { filter } = useAppSelector((state) => state.deviceTable);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { stateCid, loginCid, level } = useAppSelector((state) => state.global);
  const { isCollapse, setIsCollapse, isMD } = useMDCollapse();
  const { isLoading: deviceStatusLoading, data: deviceStatus } = useDeviceStatus(level, stateCid, loginCid);
  const [tableData, setTableData] = useState<Record<string, any>[]>([]);

  // const [restoreData] = useFilter({ filter, tableData, setDeviceTableData });
  useFilter({ filter, tableData, setDeviceTableData });

  //re-filter data after refreshing
  useEffect(() => {
    setTableData(deviceStatus?.response?.tableData || []);
    form.setFieldsValue(filter);
    form.submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceStatus?.response?.tableData]);

  // 監聽 treeSelectCellularDevice 變動時手動觸發
  useEffect(() => {
    if (!isMD && columnSetting && columnSetting.Location === false) {
      // 只有當 filter.location 不是空陣列時才清空
      if (filter?.location?.length > 0) {
        disp(setFilter({ ...filter, location: [] }));
        form.setFieldsValue({ location: [] }); // ✅ 同步 Form UI
      }
    } else if (!isMD && columnSetting && columnSetting.Description === false) {
      if (filter?.description?.length > 0) {
        disp(setFilter({ ...filter, description: [] }));
        form.setFieldsValue({ description: [] }); // ✅ 同步 Form UI
      }
    }
  }, [columnSetting.Location, columnSetting.Description, filter, setFilter, isMD]);

  // 路由 /overview 的 Filter (篩選組件)
  return (
    <Card
      title={isMD ? "Filter" : null}
      extra={
        isMD && (
          <Button
            type="text"
            icon={!isCollapse ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setIsCollapse((prev) => !prev)}
          ></Button>
        )
      }
      bodyStyle={isCollapse ? { display: "none" } : {}}
    >
      <Form onFinish={(value) => disp(setFilter(value))} form={form} layout="vertical">
        <div className="formwrap">
          <Row gutter={24}>
            <Col xs={24} sm={24} md={12} lg={6} xl={6} xxl={4}>
              <Form.Item name="name_or_id" label={t("ISMS.Device")} initialValue={[]}>
                <Select
                  loading={deviceStatusLoading}
                  disabled={deviceStatusLoading}
                  allowClear
                  autoClearSearchValue={false}
                  maxTagCount={"responsive"}
                  mode="multiple"
                  placeholder={t("ISMS.Search")}
                  onChange={() => form.submit()}
                >
                  {tableData.map((item, index) => {
                    return (
                      <Option key={index} value={(item.name ? item.name : item.id) as string}>
                        {item.name ? item.name : item.id}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={6} xl={6} xxl={3}>
              <Form.Item name="model" label={t("ISMS.Model")} initialValue={[]}>
                <Select
                  loading={deviceStatusLoading}
                  disabled={deviceStatusLoading}
                  allowClear
                  autoClearSearchValue={false}
                  mode="multiple"
                  maxTagCount={"responsive"}
                  placeholder={t("ISMS.Search")}
                  onChange={() => form.submit()}
                >
                  {deviceStatus?.response?.models?.map((item: string, index: number) => (
                    <Option key={index} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={6} xl={6} xxl={3}>
              <Form.Item name="health" label={t("ISMS.Health")} initialValue={[]}>
                <Select
                  loading={deviceStatusLoading}
                  disabled={deviceStatusLoading}
                  allowClear
                  autoClearSearchValue={false}
                  maxTagCount={"responsive"}
                  mode="multiple"
                  placeholder={t("ISMS.Search")}
                  onChange={() => form.submit()}
                >
                  {healthoptions.map((item) => (
                    <Option key={item.value} value={item.value}>
                      {item.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={6} xl={6} xxl={3}>
              <Form.Item name="strength" label={t("ISMS.Signal")} initialValue={[]}>
                <Select
                  loading={deviceStatusLoading}
                  disabled={deviceStatusLoading}
                  allowClear
                  autoClearSearchValue={false}
                  mode="multiple"
                  placeholder={t("ISMS.Search")}
                  maxTagCount={"responsive"}
                  onChange={() => form.submit()}
                >
                  {strengthoptions.map((item) => (
                    <Option key={item.value} value={item.value}>
                      {item.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={6} xl={6} xxl={3}>
              <Form.Item name="gid" label={t("ISMS.Group")} initialValue={[]}>
                <Select
                  loading={deviceStatusLoading}
                  disabled={deviceStatusLoading}
                  allowClear
                  autoClearSearchValue={false}
                  mode="multiple"
                  placeholder={t("ISMS.Search")}
                  maxTagCount={"responsive"}
                  onChange={() => form.submit()}
                >
                  {deviceStatus?.response?.groups?.map((item: string, index: number) => {
                    return (
                      <Option key={index} value={item}>
                        {item}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            {isMD ? (
              <Col xs={24} sm={24} md={12} lg={6} xl={6} xxl={3}>
                <Form.Item name="location" label={t("ISMS.Location")} initialValue={[]}>
                  <Select
                    loading={deviceStatusLoading}
                    disabled={deviceStatusLoading}
                    allowClear
                    autoClearSearchValue={false}
                    mode="multiple"
                    placeholder={t("ISMS.Search")}
                    maxTagCount={"responsive"}
                    onChange={() => form.submit()}
                  >
                    {deviceStatus?.response?.locations?.map((item: string, index: number) => {
                      return (
                        <Option key={index} value={item}>
                          {item}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
            ) : (
              columnSetting &&
              columnSetting.Location !== false && (
                <Col xs={24} sm={24} md={12} lg={6} xl={6} xxl={3}>
                  <Form.Item name="location" label={t("ISMS.Location")} initialValue={[]}>
                    <Select
                      loading={deviceStatusLoading}
                      disabled={deviceStatusLoading}
                      allowClear
                      autoClearSearchValue={false}
                      mode="multiple"
                      placeholder={t("ISMS.Search")}
                      maxTagCount={"responsive"}
                      onChange={() => form.submit()}
                    >
                      {deviceStatus?.response?.locations?.map((item: string, index: number) => {
                        return (
                          <Option key={index} value={item}>
                            {item}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              )
            )}

            {isMD ? (
              <Col xs={24} sm={24} md={12} lg={6} xl={6} xxl={3}>
                <Form.Item name="description" label={t("ISMS.Description")} initialValue={[]}>
                  <Select
                    loading={deviceStatusLoading}
                    disabled={deviceStatusLoading}
                    allowClear
                    autoClearSearchValue={false}
                    mode="multiple"
                    placeholder={t("ISMS.Search")}
                    maxTagCount={"responsive"}
                    onChange={() => form.submit()}
                  >
                    {deviceStatus?.response?.descriptions?.map((item: string, index: number) => {
                      return (
                        <Option key={index} value={item}>
                          {item}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
            ) : (
              columnSetting &&
              columnSetting.Description !== false && (
                <Col xs={24} sm={24} md={12} lg={6} xl={6} xxl={3}>
                  <Form.Item name="description" label={t("ISMS.Description")} initialValue={[]}>
                    <Select
                      loading={deviceStatusLoading}
                      disabled={deviceStatusLoading}
                      allowClear
                      autoClearSearchValue={false}
                      mode="multiple"
                      placeholder={t("ISMS.Search")}
                      maxTagCount={"responsive"}
                      onChange={() => form.submit()}
                    >
                      {deviceStatus?.response?.descriptions?.map((item: string, index: number) => {
                        return (
                          <Option key={index} value={item}>
                            {item}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              )
            )}

            <Col xs={24} sm={4} md={3} lg={3} xl={3} xxl={2}>
              <Form.Item initialValue={[]}>
                <Button
                  type={"primary"}
                  loading={deviceStatusLoading}
                  disabled={deviceStatusLoading}
                  onClick={() => {
                    // setDeviceTableData(restoreData);
                    form.resetFields();
                    disp(setFilter(initialState.filter));
                  }}
                >
                  {t("ISMS.Clear")}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </div>
      </Form>
    </Card>
  );
});

export default Filter;
